import { Router, watchBusy } from "@frui.ts/screens";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Modules } from "config";
import { action, computed, observable, reaction } from "mobx";
import EventBus, { totalCountBankTransfers } from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import NotificationService from "services/notificationService";
import emptyListResponse from "utils/emptyListResponse";
import { isFilterEmpty } from "utils/filters";
import { attachAutomaticValidator, hideValidationErrors, validate } from "@frui.ts/validation";
import ConfirmationService from "services/confirmationService";
import OptionsService from "services/optionsService";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import ListViewModel from "viewModels/listViewModel";
import PaymentBankTransferDetailViewModel from "./paymentBankTransferDetailViewModel";
import SearchTransferPaymentsRequest, {
  SearchTransferPaymentsRequestValidationRules,
} from "entities/searchTransferPaymentsRequest";
import type TransferPaymentResponse from "entities/transferPaymentResponse";
import PaymentsRepository from "repositories/paymentsRepository";
import EnumService from "services/enumService";
import { resetDirty } from "@frui.ts/dirtycheck";
import { detectAccountType } from "@edaz/utils/bankAccount";
import { BankAccountFormatType } from "entities/bankAccountFormatType";

const navigationName = "bank-transfer";

@Router.registerRoute({ name: Router.Self, route: navigationName, children: [PaymentBankTransferDetailViewModel] })
export default class PaymentBankTransferViewModel extends ListViewModel<
  TransferPaymentResponse,
  PaymentBankTransferDetailViewModel,
  ScreenBase,
  SearchTransferPaymentsRequest
> {
  private unsubscribtions: Array<() => void> = [];
  readonly modelName = "paymentBankTransfer";
  @observable totalCountBankTransfers: number;
  @observable isDisabledBankCode: boolean = false;
  countrySelectViewModel: CountrySelectViewModel;
  protected abortController?: AbortController;

  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: PaymentsRepository,
    public detailViewModelFactory: ReturnType<typeof PaymentBankTransferDetailViewModel.Factory>,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected optionsService: OptionsService,
    protected enumService: EnumService
  ) {
    super();
    this.navigationName = navigationName;
  }

  onInitialize() {
    this.initFilter();
    this.unsubscriptions.push(reaction(() => this.filter.counterpartyAccountNumber, this.onCounterpartyAccountNumberChange));
    super.onInitialize();
  }

  protected onActivate() {
    this.onTotalCountBankTransfers(this.totalCountBankTransfers);

    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscribtions.forEach(unsubsribe => unsubsribe());
      this.unsubscribtions = [];
    }

    return super.onDeactivate(isClosing);
  }

  @computed
  get isBusy() {
    return this.busyWatcher.isBusy;
  }

  @computed
  get name() {
    return this.translate("bankTransfer");
  }

  @computed
  get isFilterEmpty() {
    return isFilterEmpty(this.appliedFilter);
  }

  @action.bound
  allowItemDetail(item: any) {
    return false;
  }

  @action.bound
  onTotalCountBankTransfers(count: number): void {
    this.eventBus.publish(totalCountBankTransfers({ totalCountBankTransfers: count ?? 0 }));
  }

  @action.bound
  transformStringForTable(text: string): string {
    const words = text.split(" ");
    if (words.length > 1) {
      return `${words.slice(0, 1).join(" ")} (...)`;
    }
    return text;
  }

  @action.bound
  onCounterpartyAccountNumberChange(accNumber?: string): void {
    if (!accNumber) {
      return;
    }
    if (accNumber.includes("/")) {
      this.isDisabledBankCode = true;
      return;
    }
    this.isDisabledBankCode = false;
  }

  @action.bound
  formatCzechBankAccount(accountNumber: string) {
    if (accountNumber.includes("-")) {
      const [prefix, base] = accountNumber.split("-");
      const p = prefix.replace(/^0+/, "");
      const b = base.replace(/^0+/, "").padStart(10, "0");
      return p + b;
    } else {
      return accountNumber;
    }
  }

  @action.bound
  formatAccount(request: SearchTransferPaymentsRequest): SearchTransferPaymentsRequest {
    if (!request.counterpartyAccountNumber) {
      return request;
    }

    if (request.counterpartyAccountNumber.includes("/")) {
      const parts = request.counterpartyAccountNumber.split("/");
      request.counterpartyBank = parts[1];
      this.appliedFilter.counterpartyAccountNumber = parts[0];
      this.appliedFilter.counterpartyBank = parts[1];
      this.isDisabledBankCode = false;
    }
    if (
      detectAccountType(request.counterpartyAccountNumber) === BankAccountFormatType.CZECH ||
      request.counterpartyAccountNumber.includes("-")
    ) {
      request.counterpartyAccountNumber = this.formatCzechBankAccount(request.counterpartyAccountNumber);
    }
    return request;
  }

  protected createFilter() {
    return attachAutomaticValidator(new SearchTransferPaymentsRequest(), SearchTransferPaymentsRequestValidationRules);
  }
  applyFilter() {
    if (!isFilterEmpty(this.filter) && !validate(this.filter)) {
      return false;
    }
    this.appliedFilter = this.cloneFilterForApply(this.filter);
    this.pagingFilter.offset = 0;
    resetDirty(this.filter);
    return true;
  }

  @watchBusy
  protected async fetchData() {
    if (this.isFilterEmpty) {
      return emptyListResponse();
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
    this.abortController = new AbortController();

    let requestFilter = this.appliedFilter;
    requestFilter = this.formatAccount(requestFilter);

    const data = await this.repository.getAllBankTransferPayments(this.pagingFilter, requestFilter, this.abortController.signal);
    this.abortController = undefined;

    this.totalCountBankTransfers = data.metadata.total;

    this.onTotalCountBankTransfers(data.metadata.total);

    return data;
  }

  @watchBusy
  protected resetFilterValues(filter: SearchTransferPaymentsRequest) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }

    if (this.eventBus) {
      this.totalCountBankTransfers = 0;
      this.onTotalCountBankTransfers(0);
    }
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `payments.${code}`, size);
  }

  translateFilter(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `payments.filter.${code}`, size);
  }

  translatePlaceholder(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `payments.placeholder.${code}`, size);
  }

  @action.bound
  translatePaymentTemplate(paymentTemplateId: string) {
    const paymentTemplate = this.enumService
      .values("payment_templates")
      ?.find(paymentTemplate => paymentTemplate.id === paymentTemplateId);

    if (!paymentTemplate) {
      return;
    }

    return paymentTemplate.messageTemplate;
  }
}
